import { connect } from 'react-redux'
import {
  makeLogicNodeSelector,
  makeGetNewLogicNodeIndex,
} from 'selectors/feature/logicNodes'
import { getInDesignStatus } from 'selectors/feature/featureMetadata'
import { makeAffinityGroupSelector } from 'selectors/feature/affinityGroups'
import { getValidationDescriptions } from 'selectors/feature/features'
import { getIsEditing } from 'selectors/navigator'
import { setQueryField } from 'actions/feature/queries'
import { deleteLogicNode, resetLogicNode } from 'actions/feature/logicNodes'
import { bindActionCreators } from 'redux'

import { getOrphanQueryUris } from 'selectors/feature/functionGraphs'
import LogicSetEditor from './LogicSetEditor'

/*
Wrapping mapStateToProps in a make- function and sending that to connect
tells Redux that this gets evaluated only once per component instance,
and its returned value (the mapStateToprops fn) then is used to generate props to
pass into its child, like a normal mapStateToProps. This is needed so we
can use makeSelectors and have proper reselect memoization with that.
*/
const makeMapStateToProps = () => {
  const querySelector = makeLogicNodeSelector()
  const affinityGroupSelector = makeAffinityGroupSelector()

  const mapStateToProps = (state, ownProps) => {
    const { uri, signals } = ownProps
    const { affinityGroupNumber, highlightAffinity } = affinityGroupSelector(
      state,
      { id: uri }
    )

    const query = querySelector(state, { queryUri: uri })
    const orphanQueryUris = getOrphanQueryUris(state)
    const isOrphan = orphanQueryUris.includes(uri)
    const isEditing = getIsEditing(state)

    const {
      description,
      refsFrom,
      finalComplexQueryOperator,
      abComplexQueryOperator,
      cdComplexQueryOperator,
      delayActivate,
      delayDeactivate,
    } = query || {}

    // add ownProps.queryUri to the identifier to differentiate between instances of this component,
    // because InputBox and InputNumber components, which trigger the handler, simply pass in the 'name' attribute.
    const nextLogicSetIndex = makeGetNewLogicNodeIndex(state)
    const featureInDesign = getInDesignStatus(state)
    const allDescriptions = getValidationDescriptions(state)

    return {
      query,
      isOrphan,
      uri,
      refsFrom,
      description,
      finalComplexQueryOperator,
      abComplexQueryOperator,
      cdComplexQueryOperator,
      isEditing,
      signals,
      delayActivate,
      delayDeactivate,
      affinityGroupNumber,
      highlightAffinity,
      nextLogicSetIndex,
      featureInDesign,
      allDescriptions,
    }
  }
  return mapStateToProps
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const setFieldFromEvent = (fieldName) => (event) =>
    setQueryField(ownProps.uri, fieldName, event.target.value)
  const setFieldFromValue = (fieldName) => (value) =>
    setQueryField(ownProps.uri, fieldName, value)

  return {
    ...bindActionCreators(
      {
        deleteLogicNode,
        resetLogicNode,
        updateDescription: setFieldFromValue('description'),
        updateFinalOperator: setFieldFromEvent('finalComplexQueryOperator'),
        updateABOperator: setFieldFromEvent('abComplexQueryOperator'),
        updateCDOperator: setFieldFromEvent('cdComplexQueryOperator'),
        updateFeatureDelayTimerActivate: setFieldFromValue('delayActivate'),
        updateFeatureDelayTimerDeactivate: setFieldFromValue('delayDeactivate'),
        toggleMarkForDeletion: setFieldFromValue('markedForDeletion'),
      },
      dispatch
    ),
  }
}

const mergeProps = (propsFromState, propsFromDispatch, ownProps) => {
  const { uri } = ownProps
  const {
    deleteLogicNode: dispatchDeleteLogicNode,
    resetLogicNode: dispatchResetLogicNode,
    ...restFromDispatch
  } = propsFromDispatch
  const { query } = propsFromState

  const deleteLogicSet = () => {
    dispatchDeleteLogicNode(uri)
  }
  const resetLogicSet = () => {
    dispatchResetLogicNode(uri, query.queryType)
  }

  return {
    ...restFromDispatch,
    ...propsFromState,
    deleteLogicSet,
    resetLogicSet,
  }
}

const connected = connect(
  makeMapStateToProps,
  mapDispatchToProps,
  mergeProps
)(LogicSetEditor)

export default connected
